'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { GripVertical, Megaphone, Save, Trash2, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CttPreview } from '../CttPreview'
import { fmtDatePt } from '../cttUtils'
import type { ProcessedTheme, BrowserRow } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { CommunicationMockup } from '@shared/postui'
import type { Communication } from '@shared/postui'
import type { RawEntry, CttCampaign, PreviewEntry } from '../../trustCenterTypes'
import { CampaignEditFields } from './CampaignEditFields'
import type { CommType } from './CampaignEditFields'
import { useAIGenerate } from '../../../AI/useAIGenerate'
import { AIGenerateButton } from '../../../AI/AIGenerateButton'

type PoolImage = {
  id: string
  label: string
  base64: string
  mime: string
  url?: string
  r2Key?: string
}

const MOCKUP_ID = 'mockup'
const MOCKUP_ITEM: PoolImage = { id: MOCKUP_ID, base64: '', mime: '', label: 'Report' }
const THUMB_W = 88
const THUMB_H = 136

export type CreateConfig = {
  mode: 'create'
  processedEntry: RawEntry
  /** brand_campaigns.brand_name the promotion writes; the route defaults to 'ctt'. */
  brand?: string
  recentCampaigns?: { headline: string; description: string; image_count: number }[]
  submissionImage?: { base64: string; mime: string } | null
  autoScreenshots?: { desktop: string | null; mobile: string | null } | null
  browserRows?: BrowserRow[]
  browserRowsLoading?: boolean
}

export type EditConfig = {
  mode: 'edit'
  campaign: CttCampaign
}

export function CampaignForm({
  config,
  theme,
  getToken,
  onBack,
  onDone,
}: {
  config: CreateConfig | EditConfig
  theme: ProcessedTheme
  getToken: () => Promise<string>
  onBack: () => void
  onDone: () => void
}) {
  const isCreate = config.mode === 'create'
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const border = 'border-border'
  const muted  = 'text-muted-foreground'
  const cardBg = 'bg-card'
  const thumbBg = 'bg-secondary'

  const pe   = isCreate ? (config as CreateConfig).processedEntry : null
  const camp = isCreate ? null : (config as EditConfig).campaign

  const [editHeadline,    setEditHeadline]    = useState(pe?.headline   ?? camp?.headline   ?? '')
  const [editDescription, setEditDescription] = useState(pe?.message_content ?? camp?.description ?? '')
  // Edit mode has no stored post_type on the campaign itself (only on the
  // processed entry it was promoted from, which this form doesn't fetch) —
  // presence of a saved message_subject is the same email-vs-sms signal the
  // public portal's own campaignToCommunication fallback uses, so the editor
  // preview renders as the same bubble/email shape the report was saved as.
  const [commType,        setCommType]        = useState<CommType>(
    pe?.post_type === 'email' || (!pe && !!camp?.message_subject) ? 'email' : 'sms'
  )
  const [commSender,      setCommSender]      = useState(pe?.sender     ?? camp?.sender     ?? '')
  const [commSubject,     setCommSubject]     = useState(pe?.headline   ?? camp?.message_subject ?? '')
  const [commBody,        setCommBody]        = useState(pe?.message_content ?? camp?.message_content ?? '')

  const [nudge,          setNudge]         = useState('')
  const [saving,         setSaving]        = useState(false)
  const [deleting,       setDeleting]      = useState(false)
  const [confirmDelete,  setConfirmDelete] = useState(false)
  const [saveResult,    setSaveResult]    = useState<{ ok: boolean; msg: string } | null>(null)
  const { generate: generateCampaign, cancel: cancelGenerate, generating, generatedOnce, error: generateError } =
    useAIGenerate<{ headline?: string; description?: string }>('/api/campaigns/ctt/generate')

  const [pool, setPool] = useState<PoolImage[]>(() =>
    camp
      ? camp.images.map((img) => ({
          id: img.r2_key, label: img.alt_text || 'Image',
          base64: '', mime: '', url: img.url, r2Key: img.r2_key,
        }))
      : []
  )

  const [campaignImages, setCampaignImages] = useState<PoolImage[]>(() => {
    if (camp) {
      const existing = camp.images.map((img) => ({
        id: img.r2_key, label: img.alt_text || 'Image',
        base64: '', mime: '', url: img.url, r2Key: img.r2_key,
      }))
      // Only seed the Report card when this campaign actually has one saved
      // — otherwise every existing campaign opened for edit would show a
      // "Report" thumbnail that doesn't correspond to anything in the DB.
      return camp.message_content ? [MOCKUP_ITEM, ...existing] : existing
    }
    return [MOCKUP_ITEM]
  })

  const [loadingR2Key,     setLoadingR2Key]     = useState<string | null>(null)
  const dragIndexRef          = useRef<number | null>(null)

  const [createdAt, setCreatedAt] = useState(pe?.created_at ?? camp?.created_at ?? new Date().toISOString())

  const communication = useMemo((): Communication =>
    commType === 'email'
      ? { type: 'email', sender: commSender, subject: commSubject, receivedAt: fmtDatePt(createdAt), body: commBody }
      : { type: 'sms',   sender: commSender, receivedAt: fmtDatePt(createdAt), body: commBody }
  , [commType, commSender, commSubject, commBody, createdAt])

  const addToPool = useCallback((item: PoolImage) => {
    setPool((prev) => prev.some((p) => p.id === item.id) ? prev : [...prev, item])
  }, [])

  // Create-mode: seed pool from submission image and screenshots
  const submissionImage = isCreate ? (config as CreateConfig).submissionImage : undefined
  const autoScreenshots = isCreate ? (config as CreateConfig).autoScreenshots : undefined

  useEffect(() => {
    if (submissionImage) addToPool({ ...submissionImage, id: 'submission', label: 'Submission' })
  }, [submissionImage, addToPool])

  useEffect(() => {
    if (!autoScreenshots) return
    const desktop = autoScreenshots.desktop?.split(',')[1]
    const mobile  = autoScreenshots.mobile?.split(',')[1]
    if (desktop) addToPool({ id: 'desktop', base64: desktop, mime: 'image/png', label: 'Desktop' })
    if (mobile)  addToPool({ id: 'mobile',  base64: mobile,  mime: 'image/png', label: 'Mobile' })
  }, [autoScreenshots, addToPool])

  const toggleCampaign = useCallback((item: PoolImage) => {
    setCampaignImages((prev) =>
      prev.some((p) => p.id === item.id) ? prev.filter((p) => p.id !== item.id) : [...prev, item]
    )
  }, [])

  const removeFromCampaign = useCallback((id: string) => {
    setCampaignImages((prev) => prev.filter((p) => p.id !== id))
  }, [])

  const handleDragStart = (index: number) => { dragIndexRef.current = index }
  const handleDragOver  = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    const from = dragIndexRef.current
    if (from === null || from === index) return
    setCampaignImages((prev) => {
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(index, 0, item)
      return next
    })
    dragIndexRef.current = index
  }
  const handleDragEnd = () => { dragIndexRef.current = null }

  const handleLoadScreenshot = useCallback(async (r2Key: string, dateLabel: string) => {
    if (loadingR2Key) return
    setLoadingR2Key(r2Key)
    try {
      const token = await getToken()
      const res = await fetch('/api/screenshots/bykey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const json = await res.json()
      if (!json.success) { setSaveResult({ ok: false, msg: 'Failed to load screenshot.' }); return }
      const scenarios = json.data?.data?.buffer?.scenarios
      const dB64 = scenarios?.desktop?.screenshots?.[0]?.data ?? null
      const mB64 = scenarios?.mobile?.screenshots?.[0]?.data  ?? null
      if (!dB64 && !mB64) { setSaveResult({ ok: false, msg: 'No image data.' }); return }
      if (dB64) addToPool({ id: `${r2Key}-desktop`, base64: dB64, mime: 'image/png', label: `${dateLabel} · Desktop` })
      if (mB64) addToPool({ id: `${r2Key}-mobile`,  base64: mB64, mime: 'image/png', label: `${dateLabel} · Mobile` })
      setSaveResult(null)
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setLoadingR2Key(null)
    }
  }, [getToken, loadingR2Key, addToPool])

  const readFile = useCallback((file: File) => {
    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string
      const [header, base64] = dataUrl.split(',')
      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
      const item: PoolImage = { id: `upload-${Date.now()}`, base64, mime, label: file.name || 'Upload' }
      addToPool(item)
      setCampaignImages((prev) => [...prev, item])
    }
    reader.readAsDataURL(file)
  }, [addToPool])

  const galleryItems = useMemo(() => {
    const fixed = ['submission', 'desktop', 'mobile']
    const ordered = [
      ...fixed.map((id) => pool.find((p) => p.id === id)).filter(Boolean) as PoolImage[],
      ...pool.filter((p) => !fixed.includes(p.id)),
    ]
    const subIdx = ordered.findIndex((p) => p.id === 'submission')
    ordered.splice(subIdx + 1, 0, MOCKUP_ITEM)
    return ordered
  }, [pool])

  const browserRows     = isCreate ? ((config as CreateConfig).browserRows ?? []) : []
  const browserRowsLoad = isCreate ? ((config as CreateConfig).browserRowsLoading ?? false) : false
  const unloadedRows    = browserRows.slice(0, 3).filter(
    (r) => r.r2Key && !pool.some((p) => p.id.startsWith(r.r2Key!))
  )

  const realCampaignImages = campaignImages.filter((img) => img.id !== MOCKUP_ID)
  const hasReport          = campaignImages.some((img) => img.id === MOCKUP_ID)

  const previewEntry = useMemo((): PreviewEntry => {
    const paragraphs = editDescription.trim().split(/\n\n+/).map((p) => p.trim()).filter(Boolean)
    return {
      date:       fmtDatePt(createdAt),
      title:      editHeadline || 'Brand campaign',
      paragraphs: paragraphs.length ? paragraphs : ['Sem detalhes disponíveis.'],
      images:     realCampaignImages.map((img) => img.url ?? `data:${img.mime};base64,${img.base64}`),
    }
  }, [createdAt, editHeadline, editDescription, realCampaignImages])

  // Report card removed from campaignImages → hasReport false → don't send
  // the communication fields at all, otherwise the backend keeps storing
  // them and the public portal (PortalAlertsTab's campaignToCommunication)
  // renders the mockup anyway just because messageContent is non-empty,
  // regardless of whether the operator chose to include the Report.
  const commonBody = () => ({
    headline:       editHeadline,
    createdAt:      createdAt,
    description:    editDescription,
    sender:         hasReport ? commSender : null,
    messageSubject: hasReport ? commSubject : null,
    messageContent: hasReport ? commBody : null,
  })

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    setSaveResult(null)
    try {
      if (isCreate) {
        const [first, ...rest] = realCampaignImages
        const res = await fetch('/api/campaigns/ctt/promote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            processedEntryId: pe!.id,
            ...commonBody(),
            ...((config as CreateConfig).brand && { brand: (config as CreateConfig).brand }),
            ...(first?.base64 && { imageBase64: first.base64, imageMimeType: first.mime, imageAltText: first.label }),
          }),
        })
        const payload = await res.json()
        if (!payload.success) { setSaveResult({ ok: false, msg: payload.error ?? 'Failed to create campaign.' }); return }
        const campaignId: string | undefined = payload.data?.id
        if (campaignId && rest.length > 0) {
          await Promise.all(
            rest.filter((img) => img.base64).map((img) =>
              fetch(`/api/campaigns/ctt/${campaignId}/images`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageBase64: img.base64, imageMimeType: img.mime, imageAltText: img.label }),
              })
            )
          )
        }
        if (payload.imageWarning) {
          setSaveResult({ ok: false, msg: `Campaign created but image failed: ${payload.imageWarning}` })
          onDone()
          return
        }
        setSaveResult({ ok: true, msg: 'Brand campaign created.' })
        onDone()
        onBack()
      } else {
        const { campaign } = config as EditConfig
        const existingIds = new Set(campaign.images.map((i) => i.r2_key))
        const selectedIds = new Set(realCampaignImages.map((i) => i.r2Key).filter(Boolean) as string[])

        const patchRes = await fetch(`/api/campaigns/ctt/${campaign.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(commonBody()),
        })
        const patchPayload = await patchRes.json()
        if (!patchPayload.success) { setSaveResult({ ok: false, msg: patchPayload.error ?? 'Failed to save.' }); return }

        const toRemove = [...existingIds].filter((id) => !selectedIds.has(id))
        const newImages = realCampaignImages.filter((img) => img.base64 && !img.r2Key)
        await Promise.all([
          ...toRemove.map((r2Key) =>
            fetch(`/api/campaigns/ctt/${campaign.id}/images`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ r2Key }),
            })
          ),
          ...newImages.map((img) =>
            fetch(`/api/campaigns/ctt/${campaign.id}/images`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ imageBase64: img.base64, imageMimeType: img.mime, imageAltText: img.label }),
            })
          ),
        ])

        setSaveResult({ ok: true, msg: 'Campaign updated.' })
        onDone()
      }
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (isCreate || deleting) return
    const { campaign } = config as EditConfig
    setDeleting(true)
    setSaveResult(null)
    try {
      const res = await fetch(`/api/campaigns/ctt/${campaign.id}`, { method: 'DELETE' })
      const payload = await res.json()
      if (!payload.success) { setSaveResult({ ok: false, msg: payload.error ?? 'Failed to delete.' }); return }
      onDone()
      onBack()
    } catch {
      setSaveResult({ ok: false, msg: 'Network error.' })
    } finally {
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const handleGenerate = async () => {
    if (!isCreate) return
    const cfg = config as CreateConfig
    const data = await generateCampaign({
      headline:         cfg.processedEntry.headline,
      messageContent:   cfg.processedEntry.message_content,
      sender:           cfg.processedEntry.sender,
      postType:         cfg.processedEntry.post_type,
      hasSubmissionImage: !!cfg.processedEntry.raw_entry_r2_key,
      hasScreenshot:    pool.some((p) => p.id.includes('desktop') || p.id.includes('mobile')),
      pastCampaigns:    (cfg.recentCampaigns ?? []).slice(0, 3).map((r) => ({
        headline: r.headline, description: r.description, hasImage: r.image_count > 0,
      })),
      ...(nudge.trim() && { nudge: nudge.trim() }),
    })
    if (data) {
      setEditHeadline(data.headline ?? '')
      setEditDescription(data.description ?? '')
    }
  }

  const autoGeneratedRef = useRef(false)
  useEffect(() => {
    if (!isCreate || autoGeneratedRef.current) return
    autoGeneratedRef.current = true
    handleGenerate()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderGalleryCard = (id: string, label: string, thumbnail: React.ReactNode) => {
    const idx    = campaignImages.findIndex((c) => c.id === id)
    const isAdded = idx !== -1
    const item   = id === MOCKUP_ID ? MOCKUP_ITEM : (pool.find((p) => p.id === id) ?? null)
    return (
      <div
        key={id}
        onClick={() => item && toggleCampaign(item)}
        role="button"
        className={`relative shrink-0 rounded-[4px] overflow-hidden border-2 transition-all select-none cursor-pointer ${
          isAdded ? 'border-signal' : 'border-transparent hover:border-signal/50'
        }`}
        style={{ width: THUMB_W }}
      >
        <div className={`overflow-hidden relative ${thumbBg}`} style={{ height: THUMB_H }}>
          {thumbnail}
          {isAdded && (
            <div className="absolute top-1 left-1 h-4 min-w-[16px] px-1 rounded-sm bg-signal flex items-center justify-center shadow">
              <span className="font-mono text-[7px] text-white font-bold leading-none">{idx + 1}</span>
            </div>
          )}
        </div>
        <div className={`px-1 py-1 border-t text-center font-mono text-[7px] truncate ${border} ${cardBg} ${isAdded ? 'text-signal' : muted}`}>
          {label}
        </div>
      </div>
    )
  }

  return (
    <div className={`border px-7 py-6 ${c.line} ${c.card}`}>
      <div className={`font-mono text-[10px] uppercase tracking-widest mb-5 ${muted}`}>
        // Brand campaign
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 items-start">
        <div className="space-y-4">

          {generateError && (
            <div className="border border-destructive/40 bg-danger-surface px-3 py-2 font-mono text-[11px] text-destructive">{generateError}</div>
          )}

          <CampaignEditFields
            theme={theme}
            editHeadline={editHeadline}
            setEditHeadline={setEditHeadline}
            createdAt={createdAt}
            setCreatedAt={setCreatedAt}
            headlineAction={isCreate ? (
              <div className="flex items-center gap-1.5">
                {generatedOnce && (
                  <input
                    value={nudge}
                    onChange={(e) => setNudge(e.target.value)}
                    placeholder="Refine…"
                    disabled={generating}
                    className={`h-6 w-36 border rounded px-2 text-[10px] focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50 ${c.input}`}
                  />
                )}
                <AIGenerateButton
                  generating={generating}
                  generatedOnce={generatedOnce}
                  onGenerate={handleGenerate}
                  onCancel={cancelGenerate}
                  disabled={saving}
                  borderClass={border}
                  mutedClass={muted}
                />
              </div>
            ) : undefined}
            editDescription={editDescription}
            setEditDescription={setEditDescription}
            commType={commType}
            setCommType={setCommType}
            commSender={commSender}
            setCommSender={setCommSender}
            commSubject={commSubject}
            setCommSubject={setCommSubject}
            commBody={commBody}
            setCommBody={setCommBody}
          />

          {/* Image gallery */}
          <div className={`border p-4 ${border}`}>
            <div className={`font-mono text-[9px] uppercase tracking-widest mb-3 ${muted}`}>
              Campaign images{' '}
              <span className={'text-subtle'}>(optional)</span>
              {' · '}
              <span className="text-signal">{campaignImages.length} selected</span>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2">
              {galleryItems.map((item) => {
                if (item.id === MOCKUP_ID) {
                  return renderGalleryCard(MOCKUP_ID, 'Report', (
                    <div style={{ transform: `scale(${THUMB_W / 320})`, transformOrigin: 'top left', width: 320 }}>
                      <CommunicationMockup communication={communication} theme={theme} />
                    </div>
                  ))
                }
                const src = item.url ?? `data:${item.mime};base64,${item.base64}`
                return renderGalleryCard(item.id, item.label, (
                  <img src={src} alt={item.label} className="h-full w-full object-cover" style={{ height: THUMB_H }} />
                ))
              })}

              {browserRowsLoad && (
                <div className={`shrink-0 flex items-center justify-center border-2 border-dashed font-mono text-[8px] animate-pulse ${border} ${muted}`}
                  style={{ width: THUMB_W, height: THUMB_H + 28 }}>
                  Loading…
                </div>
              )}
              {unloadedRows.map((r) => (
                <button
                  key={r.id}
                  onClick={() => handleLoadScreenshot(r.r2Key!, fmtDatePt(r.createdAt))}
                  disabled={loadingR2Key === r.r2Key}
                  className={`shrink-0 flex flex-col items-center justify-center gap-1 border-2 border-dashed transition-colors disabled:opacity-50 font-mono text-[8px] ${border} ${muted} hover:border-signal hover:text-signal`}
                  style={{ width: THUMB_W, height: THUMB_H + 28 }}
                >
                  <span>{loadingR2Key === r.r2Key ? '⟳' : '+'}</span>
                  <span className="text-center px-1 leading-tight">{fmtDatePt(r.createdAt)}</span>
                </button>
              ))}

              <label className="block cursor-pointer shrink-0" style={{ width: THUMB_W, height: THUMB_H + 28 }}>
                <div
                  className={`flex flex-col items-center justify-center gap-1.5 h-full border-2 border-dashed transition-colors hover:border-signal hover:bg-secondary border-border`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) readFile(f) }}
                >
                  <Upload className={`h-4 w-4 ${muted}`} />
                  <span className={`font-mono text-[7px] uppercase tracking-widest ${muted}`}>Upload</span>
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f) }} />
              </label>
            </div>

            {campaignImages.length > 0 && (
              <div className={`mt-3 pt-3 border-t ${border}`}>
                <div className={`font-mono text-[8px] uppercase tracking-widest mb-2 ${muted}`}>
                  Order · drag to reorder
                </div>
                <div className="flex flex-col gap-1">
                  {campaignImages.map((img, index) => {
                    const imgSrc = img.url ?? `data:${img.mime};base64,${img.base64}`
                    return (
                      <div
                        key={img.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 rounded border px-2 py-1.5 cursor-grab active:cursor-grabbing ${border} bg-secondary`}
                      >
                        <GripVertical className={`h-3 w-3 shrink-0 ${muted}`} />
                        <span className={`font-mono text-[8px] w-3 shrink-0 text-center ${muted}`}>{index + 1}</span>
                        {img.id === MOCKUP_ID ? (
                          <div className="h-8 w-10 shrink-0 overflow-hidden rounded relative">
                            <div style={{ transform: 'scale(0.1)', transformOrigin: 'top left', width: 320 }}>
                              <CommunicationMockup communication={communication} theme={theme} />
                            </div>
                          </div>
                        ) : (
                          <img src={imgSrc} alt={img.label} className="h-8 w-10 shrink-0 rounded object-cover" />
                        )}
                        <span className={`flex-1 font-mono text-[9px] truncate ${muted}`}>{img.label}</span>
                        <button
                          onClick={() => removeFromCampaign(img.id)}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors hover:border-red-600 hover:text-destructive ${border} ${muted}`}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {saveResult && (
            <div className={`font-mono text-[11px] px-3 py-2 border ${saveResult.ok ? 'bg-success-surface border-success/40 text-success' : 'bg-danger-surface border-destructive/40 text-destructive'}`}>
              {saveResult.msg}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button variant="outline" size="sm" className="font-mono text-[11px] uppercase tracking-widest" onClick={onBack}>
              Cancel
            </Button>

            {!isCreate && (
              confirmDelete ? (
                <div className="flex items-center gap-2 ml-1">
                  <span className={`font-mono text-[10px] ${muted}`}>Delete this campaign?</span>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="border border-destructive/40 bg-danger-surface px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-destructive hover:bg-danger-surface disabled:opacity-50 transition-colors"
                  >
                    {deleting ? 'Deleting…' : 'Confirm delete'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className={`font-mono text-[9px] uppercase tracking-widest ${muted} hover:text-signal`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  className={`flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-colors disabled:opacity-50 ${border} ${muted} hover:border-destructive/40 hover:text-destructive`}
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              )
            )}

            <Button
              size="sm"
              className="ml-auto font-mono text-[11px] uppercase tracking-widest gap-1.5"
              disabled={saving || !editHeadline?.trim()}
              onClick={handleSave}
            >
              {isCreate ? <Megaphone className="h-3 w-3" /> : <Save className="h-3 w-3" />}
              {saving
                ? (isCreate ? 'Creating…' : 'Saving…')
                : (isCreate ? 'Create campaign' : 'Save changes')}
            </Button>
          </div>
        </div>

        <div className="lg:sticky lg:top-6">
          <CttPreview
            entry={previewEntry}
            tab={isCreate ? 'Processed entries' : 'Brand campaigns'}
            reportSlot={hasReport ? <CommunicationMockup communication={communication} theme={theme} /> : undefined}
          />
        </div>
      </div>
    </div>
  )
}
